import type {
  InPlaceRestorePreparationStatus,
  InPlaceRestorePreview,
  SnapshotCompareChange,
  SnapshotCompareEntityType,
} from "@board-preserve/contracts";

const VISIBLE_CHANGE_LIMIT_PER_GROUP = 50;

const STRUCTURE_ORDER: readonly SnapshotCompareEntityType[] = [
  "board",
  "list",
  "card",
  "checklist",
  "custom_field",
  "label",
  "member",
  "membership",
  "attachment",
  "comment",
  "activity",
];

export interface InPlaceChangeGroup {
  changed: number;
  details: InPlaceChangeDetail[];
  hiddenDetailCount: number;
  label: string;
  liveOnly: number;
  moved: number;
  restoreMissing: number;
  type: SnapshotCompareEntityType;
}

export interface InPlaceChangeDetail {
  changedFields: string[];
  entityId: string;
  label: string;
  outcome: "changed" | "live_only" | "missing_live" | "moved";
  outcomeLabel: string;
}

export interface InPlacePreparationPresentation {
  active: boolean;
  label: string;
  message: string;
  readyForConfirmation: boolean;
  restartRequired: boolean;
  tone: "error" | "neutral" | "progress" | "ready";
}

export interface InPlaceRestoreAcknowledgements {
  changesOriginalBoard: boolean;
  comparisonReviewed: boolean;
  extrasRemainIntact: boolean;
  fidelityReviewed: boolean;
  noDeleteEndpoints: boolean;
  safetyBackupCompleted: boolean;
}

export function isInPlaceExecutionBuildEnabled(
  value: string | undefined,
): boolean {
  return value === "true";
}

export function canSubmitInPlaceConfirmation(input: {
  acknowledgements: InPlaceRestoreAcknowledgements;
  boardName: string;
  executionEnabled: boolean;
  preparationReady: boolean;
  submitting: boolean;
  typedBoardName: string;
}): boolean {
  return (
    input.executionEnabled &&
    input.preparationReady &&
    !input.submitting &&
    input.typedBoardName === input.boardName &&
    Object.values(input.acknowledgements).every(Boolean)
  );
}

export function inPlacePreparationPresentation(
  status: InPlaceRestorePreparationStatus,
  operationCount: number,
): InPlacePreparationPresentation {
  switch (status) {
    case "safety_backup_pending":
      return {
        active: true,
        label: "Safety backup queued",
        message:
          "Waiting for capture capacity. The live board has not been changed.",
        readyForConfirmation: false,
        restartRequired: false,
        tone: "progress",
      };
    case "safety_backup_running":
      return {
        active: true,
        label: "Safety backup running",
        message: "Capturing the live board now. Restore writes remain locked.",
        readyForConfirmation: false,
        restartRequired: false,
        tone: "progress",
      };
    case "ready_for_confirmation":
      return {
        active: false,
        label: "Safety backup complete",
        message: `${operationCount} supported operation${operationCount === 1 ? " is" : "s are"} ready for final review. No restore writes have been made.`,
        readyForConfirmation: true,
        restartRequired: false,
        tone: "ready",
      };
    case "confirmed":
      return {
        active: false,
        label: "Prepared plan confirmed",
        message:
          "The immutable plan was handed to the original-board restore job. Follow its live progress and final outcome below.",
        readyForConfirmation: true,
        restartRequired: false,
        tone: "ready",
      };
    case "failed":
      return {
        active: false,
        label: "Safety backup failed",
        message:
          "No restore writes were made. Compare the board again before retrying.",
        readyForConfirmation: false,
        restartRequired: true,
        tone: "error",
      };
    case "expired":
      return {
        active: false,
        label: "Preparation expired",
        message:
          "No restore writes were made. Compare the board again to create a fresh safety boundary.",
        readyForConfirmation: false,
        restartRequired: true,
        tone: "neutral",
      };
  }
  throw new Error("Unsupported in-place preparation status.");
}

function entityLabel(type: SnapshotCompareEntityType): string {
  return {
    activity: "Activity records",
    attachment: "Attachments",
    board: "Board settings",
    card: "Cards",
    checklist: "Checklists",
    comment: "Comments",
    custom_field: "Custom fields",
    label: "Labels",
    list: "Lists",
    member: "Members",
    membership: "Memberships",
  }[type];
}

function readableFieldName(field: string): string {
  const known: Record<string, string> = {
    cardId: "Card",
    checklistIds: "Checklists",
    closed: "Archived state",
    cover: "Cover",
    customFieldItems: "Custom fields",
    due: "Due date",
    dueComplete: "Due status",
    dueReminder: "Due reminder",
    labelIds: "Labels",
    listId: "List",
    memberIds: "Members",
    name: "Name",
    position: "Position",
    start: "Start date",
    voterMemberIds: "Votes",
  };
  return (
    known[field] ??
    field
      .replaceAll("_", " ")
      .replace(/([a-z0-9])([A-Z])/gu, "$1 $2")
      .replace(/^./u, (character) => character.toUpperCase())
  );
}

function changeDetail(change: SnapshotCompareChange): InPlaceChangeDetail {
  const presentation = {
    added: {
      outcome: "live_only" as const,
      outcomeLabel: "Only on live board",
    },
    changed: {
      outcome: "changed" as const,
      outcomeLabel: "Changed on live board",
    },
    moved: {
      outcome: "moved" as const,
      outcomeLabel: "Moved on live board",
    },
    removed: {
      outcome: "missing_live" as const,
      outcomeLabel: "Missing from live board",
    },
  }[change.change];
  return {
    changedFields: change.changedFields.map(readableFieldName),
    entityId: change.entityId,
    label: change.label,
    ...presentation,
  };
}

export function inPlaceRestoreChangeGroups(
  preview: InPlaceRestorePreview,
): InPlaceChangeGroup[] {
  return STRUCTURE_ORDER.map((type) => {
    const counts = preview.comparison.collections[type];
    const matchingDetails = preview.comparison.changes
      .filter((change) => change.entityType === type)
      .map(changeDetail)
      .toSorted((left, right) => {
        const priority = {
          missing_live: 0,
          changed: 1,
          moved: 2,
          live_only: 3,
        };
        return (
          priority[left.outcome] - priority[right.outcome] ||
          left.label.localeCompare(right.label)
        );
      });
    return {
      changed: counts.changed,
      details: matchingDetails.slice(0, VISIBLE_CHANGE_LIMIT_PER_GROUP),
      hiddenDetailCount: Math.max(
        0,
        matchingDetails.length - VISIBLE_CHANGE_LIMIT_PER_GROUP,
      ),
      label: entityLabel(type),
      // The comparison is target backup -> live board. An item added in the
      // live side exists only on the current board; an item removed from that
      // direction is missing live and may be recreated by the restore plan.
      liveOnly: counts.added,
      moved: counts.moved,
      restoreMissing: counts.removed,
      type,
    };
  }).filter(
    ({ changed, liveOnly, moved, restoreMissing }) =>
      changed + liveOnly + moved + restoreMissing > 0,
  );
}
